import React, { useEffect, useState } from 'react';
import EcoImg from 'eco-design/base/eco-img';
import EfButton from '@elem/ui/ef-button';
import styles from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { useRouter as nextUseRouter } from 'next/router';
import { IPageStatus } from '@elem/compPage/delta-3-max-series/config/IPageStatus';
import { saveCookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import { productConf } from '../../productData';
import { useProductStore } from '../../../ProductProvider';
import { getMemberListProducts } from '../../../service';
import { XIcon } from '../icons';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import getLocal from '@/app/utils/getLocal';

const ProductList: React.FC = () => {
    const { isMobile } = useGlobal();
    const { locale } = getLocal();
    const router = nextUseRouter();
    const { _timer } = router.query;
    const [productList, setProductList] = useState<any>([]);
    const { t } = useLang('Delta3MaxSeriesPreheat.ProductList');
    const { pageStatus } = useProductStore();
    const getVids = () => productConf[locale]?.map((item) => item.variantId);
    const [showSubTitle, setShowSubTitle] = useState<boolean>(true);
    const showEndTime = new Date('2025/09/17 14:00:00 GMT+8').getTime();
    const startTime = _timer ? new Date(`${_timer} GMT+8`).getTime() : 0;

    const getShareLink = () => {
        const statusALink = `https://x.com/EcoFlowJapan`;

        const shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(t('share_text'))}`;
        if (pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB) {
            return statusALink;
        }
        return shareLink;
    };

    const productConfigs: any = {
        '52158000202095': {
            desc: '2,048Wh|3,000W 出力（サージ6,000W）\n1,500W AC入力&1,000Wソーラー入力\n<10ms 電源自動切り替え機能\n5年間の保証付き',
            learnUrl: 'https://jp.ecoflow.com/products/delta-3-max-plus-800w-alternator-charger',
        },
        '52157995680111': {
            desc: '2,048Wh|3,000W 出力（サージ6,000W）\n1,500W AC入力&1,000Wソーラー入力\n<10ms 電源自動切り替え機能\n5年間の保証付き',
            learnUrl: 'https://jp.ecoflow.com/products/delta-3-max-plus-400w-solar-panel',
        },
        '52158005084527': {
            desc: '4,096Wh|3,000W 出力（サージ6,000W）\n1,500W AC入力&1,000Wソーラー入力\n<10ms 電源自動切り替え機能\n5年間の保証付き',
            learnUrl: 'https://jp.ecoflow.com/products/delta-3-max-plus-extra-battery-400w-solar-panel-bundle',
        },
        '52158022943087': {
            desc: '2,048Wh|3,000W 出力（サージ6,000W）\n1,500W AC入力&1,000Wソーラー入力\n<10ms 電源自動切り替え機能\n5年間の保証付き',
            learnUrl: 'https://jp.ecoflow.com/products/delta-3-max-400w-solar-panel',
        },
    };

    const getDiscount = (price: number, comparePrice: number) => {
        if (price <= 0) return 0;
        if (comparePrice <= 0) return 0;

        const discount = Math.round((1 - price / comparePrice) * 100);
        return discount;
    };

    const getEcocredits = (price: number) => {
        if (price <= 0) return 0;
        return Math.round((price / 150) * 5)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const getProductData = () => {
        if (!getVids()) return;

        getMemberListProducts(
            {
                variantIds: getVids(),
            },
            locale,
        ).then((res) => {
            if (res.code === '0' && res.data) {
                const list = Object.keys(productConfigs).map((item: any) => {
                    const findItem = res.data.find((v: any) => v.variantId === item) || {};
                    return {
                        ...productConfigs[item],
                        ...findItem,
                        buyUrl: `https://${locale}.ecoflow.com/cart/${findItem.variantId}:1?channel=buy_button`,
                    };
                });

                setProductList(list);
            }
        });
    };

    const getCurentTime = (startTime: number) => {
        if (!_timer) return;

        const diff = Math.max(0, showEndTime - startTime);

        if (diff === 0) {
            setShowSubTitle(false);
        } else {
            setTimeout(() => {
                getCurentTime(startTime + 1000);
            }, 1000);
        }
    };

    const countdownShowSubTitle = () => {
        const now = Date.now();
        const diff = Math.max(0, showEndTime - now);

        if (diff === 0) {
            setShowSubTitle(false);
        } else {
            setTimeout(countdownShowSubTitle, 1000);
        }
    };

    const handleDetail = (item: any) => {
        saveCookieForShopify(item.learnUrl, locale);
        gtm.push({
            event: 'uaEvent',
            event_name: 'buy',
            product_name: item.productTitle,
            position: 'Product Preview',
        });
    };

    const handleToBuy = (item: any) => {
        saveCookieForShopify(item.buyUrl, locale);
        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${t('cta_btn_2')}_${item.productTitle}`,
        });
    };

    const handleShare = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_join_social',
            method: 'X',
            position: '新製品DELTA3MaxPlus発売記念キャンペーン',
            button_name: t('share_cta'),
        });
    };

    const getShareImg = () => {
        if (isMobile) {
            if (pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB) {
                return `https://websiteoss.ecoflow.com/upload/image/1756264518498/1370.jpg`;
            }
            return `https://websiteoss.ecoflow.com/upload/image/1756969144363/share2_m.jpg`;
        }
        if (pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB) {
            return `https://websiteoss.ecoflow.com/upload/image/1756264449289/1200_540-pc.png`;
        }
        return `https://websiteoss.ecoflow.com/upload/image/1756969136491/1200_540_share2-pc.jpg`;
    };

    useEffect(() => {
        getProductData();
        if (!_timer) {
            countdownShowSubTitle();
        } else {
            getCurentTime(startTime);
        }
    }, []);

    return (
        <section className={styles.ProductList} id="d3mProductList">
            <div className="title-box">
                <div className="title1">{t('title')}</div>
                {showSubTitle && <div className="subtitle">{t('subtitle')}</div>}
            </div>
            <div className="product-content">
                {productList.map((item: any, index: number) => (
                    <div className="item" key={item.variantId}>
                        <div className="item-top">
                            {pageStatus !== IPageStatus.statusA ? (
                                <div className="item-discount">
                                    <span className="left-off">
                                        <span className="num">{getDiscount(item.price, item.compareAtPrice)}%</span>
                                        <span className="unit">OFF</span>
                                    </span>

                                    <div className={`item-multiple-credit item-multiple-credit-${index}`}>
                                        <span className="multiple">5倍</span>
                                        <span className="credit">{getEcocredits(item.price)}ポイント</span>
                                    </div>
                                </div>
                            ) : null}

                            <div className="item-pic">
                                <EcoImg pc={item.img}></EcoImg>
                            </div>

                            <div className="item-title-box">
                                <p className="item-title">{item.productTitle}</p>
                                <p className="item-subtitle">{item.desc}</p>
                            </div>
                        </div>
                        <div className="item-bottom">
                            {pageStatus !== IPageStatus.statusA && (
                                <div className="item-price">
                                    <div className="limited-price">
                                        <span className="limited-label">限定価格：</span>￥
                                        {parseInt(item.price)
                                            .toString()
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    </div>
                                    <div className="common-price">
                                        通常価格：￥
                                        {parseInt(item.compareAtPrice)
                                            .toString()
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    </div>
                                </div>
                            )}

                            {pageStatus === IPageStatus.statusA && <EfButton className="item-btn">{t('cta_btn')}</EfButton>}
                            {pageStatus !== IPageStatus.statusA && (
                                <>
                                    <EfButton className="item-btn detail-btn" href={item.learnUrl} onClick={() => handleDetail(item)}>
                                        {t('cta_btn_1')}
                                    </EfButton>
                                    <EfButton
                                        className={`item-btn buy-btn ${item.inventoryQuantity ? '' : 'disabled'}`}
                                        href={item.buyUrl}
                                        onClick={() => handleToBuy(item)}
                                    >
                                        {t('cta_btn_2')}
                                    </EfButton>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="product-tips">{t('product_tips')}</div>
            <div className="d3m-share">
                <div className="title-box-2">
                    <div className="title1">
                        {pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB
                            ? t.rich('share_title', {
                                  text1: (str: string) => <span>{str}</span>,
                              })
                            : t.rich('share_title2', {
                                  text1: (str: string) => <span>{str}</span>,
                              })}
                    </div>
                    <div className="subtitle">
                        {pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB
                            ? t.rich('share_subtitle', {
                                  text1: (str: string) => <span>{str}</span>,
                              })
                            : t.rich('share_subtitle2', {
                                  text1: (str: string) => <span>{str}</span>,
                              })}
                    </div>
                    <EfButton className="cta-btn" onClick={handleShare} href={getShareLink()} target="_blank" rel="noopener noreferrer">
                        {pageStatus === IPageStatus.statusA || pageStatus === IPageStatus.statusB ? (
                            t('share_cta')
                        ) : (
                            <>
                                <XIcon></XIcon>
                                <span style={{ marginLeft: '5px' }}>{t('share_cta2')}</span>
                            </>
                        )}
                    </EfButton>
                </div>
                <div className="prize-box">
                    <img className="prize-img" src={getShareImg()}></img>
                </div>
            </div>
        </section>
    );
};

export default ProductList;
